import React, { useCallback, useEffect, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLogin, setLogout } from "state";
import Layout from "components/Layout";
import ConfirmDialog from "components/ConfirmDialog";
import api from "utils/api";
import { formatPrice } from "constants/massages";
import ProfileSettings, { PasswordSettings } from "./ProfileSettings";
import { APP_BRAND } from "utils/brand";
import { queueAuthSuccessPopup } from "utils/authSuccessPopup";
import "styles/konto.css";

const KontoPage = () => {
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    dispatch(setLogout());
    queueAuthSuccessPopup("logout");
    navigate("/");
  };

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [referral, setReferral] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const token = useSelector((s) => s.token);

  const loadReferral = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data?.user) {
        dispatch(setLogin({ token, user: data.user }));
        setReferral({
          link: data.user.referralLink,
          balanceCents: data.user.referralBalanceCents || 0,
          useCount: data.user.referralUseCount || 0,
        });
      }
    } catch {
      /* optional panel */
    }
  }, [dispatch, token]);

  const copyReferralLink = async () => {
    if (!referral?.link) return;
    try {
      await navigator.clipboard.writeText(referral.link);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      setReferralCopied(false);
    }
  };

  const referralShareText = referral?.link
    ? `Gönn dir eine wohltuende Auszeit mit Dr. Hoffmann!
Registriere dich jetzt über meinen persönlichen Link und sichere dir deine nächste Massage:
 ${referral.link}`
    : "";
  const referralShareSubject = "Dr. Hoffmann Massage Empfehlung";
  const encodedReferralText = encodeURIComponent(referralShareText);
  const encodedReferralLink = encodeURIComponent(referral?.link || "");
  const encodedReferralSubject = encodeURIComponent(referralShareSubject);

  const shareViaInstagram = async () => {
    if (!referralShareText) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: referralShareSubject,
          text: referralShareText,
          url: referral.link,
        });
        return;
      }
      await navigator.clipboard.writeText(referralShareText);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      /* still open Instagram below */
    }
    window.open("https://www.instagram.com/direct/inbox/", "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    loadReferral();
  }, [loadReferral]);

  useEffect(() => {
    if (location.hash !== "#konto-referral-card" || !referral?.link) return undefined;

    const scrollToReferral = () => {
      const target = document.getElementById("konto-referral-card");
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const timer = window.setTimeout(scrollToReferral, 0);
    return () => window.clearTimeout(timer);
  }, [location.hash, referral?.link]);

  const confirmDeleteAccount = async () => {
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const { data } = await api.post("/auth/request-account-deletion", {
        brand: APP_BRAND,
      });
      setDeleteOpen(false);
      setDeleteMsg({
        kind: "success",
        text:
          data.message ||
          "Eine Bestätigungs-E-Mail wurde an deine Adresse gesendet.",
      });
    } catch (err) {
      setDeleteMsg({
        kind: "error",
        text: err?.response?.data?.message || "Anfrage fehlgeschlagen.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const referralCard = referral?.link ? (
    <section
      id="konto-referral-card"
      className="konto-referral-card"
      aria-labelledby="konto-referral-title"
    >
      <div className="konto-referral-head">
        <h2 id="konto-referral-title">Freunde empfehlen</h2>
        <p>
          Teile deinen persönlichen Link. Wenn sich jemand darüber registriert und
          erstmals eine Massage bezahlt, erhältst du einmalig{" "}
          <strong>5&nbsp;€ Guthaben</strong> für deine nächste Buchung.{" "}
          <Link to="/empfehlungsprogramm">So funktioniert’s</Link>
        </p>
      </div>
      <div className="konto-referral-stats">
        <div>
          <span className="konto-referral-label">Guthaben</span>
          <strong>{formatPrice(referral.balanceCents)}</strong>
          <small>Nur für Massagen auf dieser Website</small>
        </div>
        <div>
          <span className="konto-referral-label">Link genutzt</span>
          <strong>{referral.useCount}×</strong>
          <small>Registrierungen über deinen Link</small>
        </div>
      </div>
      <div className="konto-referral-link-row">
        <input
          type="text"
          readOnly
          value={referral.link}
          aria-label="Dein Empfehlungslink"
        />
        <button type="button" className="btn-primary" onClick={copyReferralLink}>
          {referralCopied ? "Kopiert" : "Link kopieren"}
        </button>
        <div className="konto-referral-share" aria-label="Empfehlungslink teilen">
          <a
            href={`https://wa.me/?text=${encodedReferralText}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Per WhatsApp teilen"
            title="WhatsApp"
          >
            <WhatsAppIcon aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={shareViaInstagram}
            aria-label="Per Instagram teilen"
            title="Instagram"
          >
            <InstagramIcon aria-hidden="true" />
          </button>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedReferralLink}&quote=${encodedReferralText}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Per Facebook teilen"
            title="Facebook"
          >
            <FacebookOutlinedIcon aria-hidden="true" />
          </a>
          <a
            href={`mailto:?subject=${encodedReferralSubject}&body=${encodedReferralText}`}
            aria-label="Per E-Mail teilen"
            title="E-Mail"
          >
            <EmailOutlinedIcon aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  ) : null;

  return (
    <Layout>
      <section className="konto-page">
        <div className="konto-shell">
          <header className="konto-header">
            <div>
              <h1>
                Mein <em>Konto</em>
              </h1>
              <p>
                Willkommen{user?.firstName ? `, ${user.firstName}` : ""}. Hier kannst du
                deine Profildaten und dein Passwort verwalten.
              </p>
            </div>
            <Link to="/konto/termine" className="btn-primary">
              Meine Termine
            </Link>
          </header>

          <ProfileSettings
            brand={APP_BRAND}
            passwordSection={<PasswordSettings user={user} embedded />}
          />

          {referralCard}

          {deleteMsg && (
            <div className={`auth-alert is-${deleteMsg.kind}`}>{deleteMsg.text}</div>
          )}

          <div className="konto-footer">
            <button
              type="button"
              className="btn-ghost konto-logout"
              onClick={() => setLogoutOpen(true)}
            >
              Abmelden
            </button>
            <button
              type="button"
              className="konto-delete"
              onClick={() => setDeleteOpen(true)}
            >
              Konto löschen
            </button>
          </div>
        </div>

        <ConfirmDialog
          open={logoutOpen}
          title="Abmelden?"
          message="Möchtest du dich wirklich abmelden?"
          confirmLabel="Abmelden"
          cancelLabel="Abbrechen"
          danger={false}
          onConfirm={() => {
            setLogoutOpen(false);
            handleLogout();
          }}
          onCancel={() => setLogoutOpen(false)}
        />
        <ConfirmDialog
          open={deleteOpen}
          title="Konto wirklich löschen?"
          message="Möchtest du dein Konto wirklich löschen? Du erhältst eine E-Mail mit einem Bestätigungslink."
          confirmLabel="Ja, löschen"
          cancelLabel="Abbrechen"
          busy={deleting}
          busyLabel="Wird gesendet…"
          onConfirm={confirmDeleteAccount}
          onCancel={() => !deleting && setDeleteOpen(false)}
        />
      </section>
    </Layout>
  );
};

export default KontoPage;
